import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as annotationApi from '../services/annotationApi';
import type {
  AnnotationTask,
  Utterance,
  TaskReview,
  PhotoLabels,
} from '../types/annotation';
import type { ListTasksParams } from '../services/annotationApi';

// ─── Task List ─────────────────────────────────────────────────────────────────

export function useAnnotationTaskList(params?: ListTasksParams) {
  return useQuery({
    queryKey: ['annotation-tasks', params],
    queryFn: () => annotationApi.listTasks(params),
  });
}

// ─── Single Task ───────────────────────────────────────────────────────────────

export function useAnnotationTask(taskId: string | undefined) {
  return useQuery({
    queryKey: ['annotation-task', taskId],
    queryFn: () => annotationApi.getTask(taskId!),
    enabled: !!taskId,
  });
}

// ─── Create Utterance ──────────────────────────────────────────────────────────

export function useCreateUtterance(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Utterance>) => annotationApi.createUtterance(taskId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['annotation-task', taskId] }),
  });
}

// ─── Update Utterance (optimistic) ────────────────────────────────────────────

export function useUpdateUtterance(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      utteranceId,
      data,
    }: {
      utteranceId: string;
      data: Partial<Utterance>;
    }) => annotationApi.updateUtterance(taskId, utteranceId, data),
    onMutate: async ({ utteranceId, data }) => {
      await qc.cancelQueries({ queryKey: ['annotation-task', taskId] });
      const prev = qc.getQueryData<AnnotationTask>(['annotation-task', taskId]);
      qc.setQueryData<AnnotationTask | undefined>(
        ['annotation-task', taskId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            utterances: old.utterances.map((u) =>
              u.utteranceId === utteranceId ? { ...u, ...data } : u,
            ),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(['annotation-task', taskId], ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
    },
  });
}

// ─── Delete Utterance ──────────────────────────────────────────────────────────

export function useDeleteUtterance(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (utteranceId: string) =>
      annotationApi.deleteUtterance(taskId, utteranceId),
    onMutate: async (utteranceId) => {
      await qc.cancelQueries({ queryKey: ['annotation-task', taskId] });
      const prev = qc.getQueryData<AnnotationTask>(['annotation-task', taskId]);
      qc.setQueryData<AnnotationTask | undefined>(
        ['annotation-task', taskId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            utterances: old.utterances.filter((u) => u.utteranceId !== utteranceId),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(['annotation-task', taskId], ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
    },
  });
}

// ─── Create Entity ─────────────────────────────────────────────────────────────

export function useCreateEntity(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      utteranceId,
      data,
    }: {
      utteranceId: string;
      data: { entityType: string; entityName: string; sentiment?: string };
    }) => annotationApi.createEntity(utteranceId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['annotation-task', taskId] }),
  });
}

// ─── Delete Entity ─────────────────────────────────────────────────────────────

export function useDeleteEntity(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      utteranceId,
      entityId,
    }: {
      utteranceId: string;
      entityId: string;
    }) => annotationApi.deleteEntity(utteranceId, entityId),
    onMutate: async ({ utteranceId, entityId }) => {
      await qc.cancelQueries({ queryKey: ['annotation-task', taskId] });
      const prev = qc.getQueryData<AnnotationTask>(['annotation-task', taskId]);
      qc.setQueryData<AnnotationTask | undefined>(
        ['annotation-task', taskId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            utterances: old.utterances.map((u) =>
              u.utteranceId === utteranceId
                ? { ...u, entities: u.entities.filter((e) => e.entityId !== entityId) }
                : u,
            ),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(['annotation-task', taskId], ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
    },
  });
}

// ─── Submit Task ───────────────────────────────────────────────────────────────

export function useSubmitTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => annotationApi.submitTask(taskId),
    // The submit endpoint returns `{ task: <bare row> }`, not a hydrated
    // AnnotationTask — so we can't seed the cache from the response. Invalidate
    // the per-task key instead so the workspace refetches the canonical
    // hydrated task (with utterances/photos/transcripts/latestReview) and the
    // useEffect that syncs into the Zustand store flips `state.task.status` to
    // 'submitted'. Without this, Submit / Add Segment / autosave stay live and
    // the server starts 403'ing every edit ("Save failed").
    onSuccess: (_data, taskId) => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
    },
  });
}

// ─── Review Task ───────────────────────────────────────────────────────────────

export function useReviewTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      data,
    }: {
      taskId: string;
      data: { status: 'approved' | 'rejected' | 'revision_needed'; comments?: string };
    }) => annotationApi.reviewTask(taskId, data),
    onSuccess: (_review: TaskReview, { taskId }) => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
    },
  });
}

export function useFinalReviewTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      data,
    }: {
      taskId: string;
      data: { status: 'final_accepted' | 'final_rejected' | 'revision_needed'; comments?: string };
    }) => annotationApi.finalReviewTask(taskId, data),
    onSuccess: (_review: TaskReview, { taskId }) => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
    },
  });
}

export function useReopenTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      data,
    }: {
      taskId: string;
      data: { target: 'submitted' | 'revision_needed' | 'approved'; comments: string };
    }) => annotationApi.reopenTask(taskId, data),
    onSuccess: (_review: TaskReview, { taskId }) => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
    },
  });
}

// ─── Claim / Unclaim Task ──────────────────────────────────────────────────────

export function useClaimTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => annotationApi.claimTask(taskId),
    onSuccess: (_task, taskId) => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
    },
  });
}

export function useUnclaimTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => annotationApi.unclaimTask(taskId),
    onSuccess: (_task, taskId) => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
      qc.invalidateQueries({ queryKey: ['annotation-tasks'] });
    },
  });
}

// ─── Upsert Photo Labels ───────────────────────────────────────────────────────

export function useUpsertPhotoLabels(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      photoId,
      data,
    }: {
      photoId: string;
      data: Partial<PhotoLabels>;
    }) => annotationApi.upsertPhotoLabels(photoId, data),
    onMutate: async ({ photoId, data }) => {
      await qc.cancelQueries({ queryKey: ['annotation-task', taskId] });
      const prev = qc.getQueryData<AnnotationTask>(['annotation-task', taskId]);
      qc.setQueryData<AnnotationTask | undefined>(
        ['annotation-task', taskId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            photos: old.photos.map((p) => {
              if (p.photoId !== photoId) return p;
              const existingLabels = p.labels ?? ({} as PhotoLabels);
              return { ...p, labels: { ...existingLabels, ...data } as PhotoLabels };
            }),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(['annotation-task', taskId], ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
    },
  });
}
